import React, { useState } from 'react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import './FolderTreeItem.css';

const FOLDER_ICONS = {
  'folder': '📁',
  'briefcase': '💼',
  'star': '⭐',
  'heart': '❤️',
  'bookmark': '🔖',
  'archive': '📦',
  'music': '🎵',
  'video': '🎬',
  'meeting': '👥',
  'personal': '👤',
  'work': '💻',
  'notes': '📝'
};

function FolderTreeItem({
  folder,
  level,
  currentFolderId,
  onSelect,
  onCreateSubfolder,
  onEdit,
  onDelete,
  onDrop,
  onMove
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const hasChildren = folder.children && folder.children.length > 0;
  const isActive = currentFolderId === folder.id;

  const handleFolderClick = (e) => {
    e.stopPropagation();
    onSelect(folder.id);
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    setIsChecked(e.target.checked);
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData('folder', folder.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if dragging a folder
    const draggedFolderId = e.dataTransfer.types.includes('folder');
    if (draggedFolderId) {
      // Don't allow dropping on self
      return;
    }

    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Check if dropping a folder
    const draggedFolderId = e.dataTransfer.getData('folder');
    if (draggedFolderId && draggedFolderId !== folder.id) {
      onMove(draggedFolderId, folder.id);
      return;
    }

    // Otherwise it's transcripts
    onDrop(e, folder.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(folder);
    setIsChecked(false); // Uncheck after action
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Delete folder "${folder.name}"? Transcripts will be moved to the parent folder.`)) {
      onDelete(folder.id);
      setIsChecked(false);
    }
  };

  const getFolderIcon = () => {
    return FOLDER_ICONS[folder.icon] || FOLDER_ICONS['folder'];
  };

  return (
    <div className="folder-tree-item-container">
      <div
        className={`folder-tree-item ${isActive ? 'active' : ''} ${isDragOver ? 'drag-over' : ''}`}
        style={{ paddingLeft: `${level * 12 + 12}px` }}

        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        draggable
        onDragStart={handleDragStart}
      >
        {/* Checkbox for folder actions */}
        <input
          type="checkbox"
          className="folder-checkbox"
          checked={isChecked}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          title="Select folder"
        />

        <button
          className={`expand-btn ${hasChildren ? 'visible' : ''}`}
          onClick={handleExpandClick}
        >
          {hasChildren && (
            isExpanded ? (
              <ChevronDownIcon className="expand-icon" />
            ) : (
              <ChevronRightIcon className="expand-icon" />
            )
          )}
        </button>

        <div 
          className="folder-content"
          onClick={handleFolderClick}
        >
          <span
            className="folder-icon"
            style={{ color: folder.color || '#4ECDC4' }}
          >
            {getFolderIcon()}
          </span>

          <span className="folder-name" title={folder.name}>
            {folder.name}
          </span>
        </div>

        {/* Action buttons - shown when checked */}
        {isChecked && (
          <div className="folder-actions-visible">
            <button
              className="action-btn edit"
              onClick={handleEdit}
              title="Edit folder"
            >
              <PencilIcon className="action-icon" />
              Edit
            </button>
            <button
              className="action-btn delete"
              onClick={handleDelete}
              title="Delete folder"
            >
              <TrashIcon className="action-icon" />
              Delete
            </button>
          </div>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="folder-children">
          {folder.children.map(child => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              currentFolderId={currentFolderId}
              onSelect={onSelect}
              onCreateSubfolder={onCreateSubfolder}
              onEdit={onEdit}
              onDelete={onDelete}
              onDrop={onDrop}
              onMove={onMove}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default FolderTreeItem;
